import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { BarChart3, DollarSign, BookOpen, Users } from 'lucide-react';
import { TLManagerService } from '../../services/tlManagerService';

const TLManagerDashboard = () => {
  const navigate = useNavigate();

  const { data: performanceData, isLoading: performanceLoading } = useQuery({
    queryKey: ['tlManagerPerformance'],
    queryFn: () => TLManagerService.getPerformanceData(),
  });

  const { data: bookedOrdersData, isLoading: bookedLoading } = useQuery({
    queryKey: ['tlManagerBookedOrders'],
    queryFn: () => TLManagerService.getBookedOrders(),
  });

  const { data: paymentVerificationsData, isLoading: paymentsLoading } = useQuery({
    queryKey: ['tlManagerPaymentVerifications'],
    queryFn: () => TLManagerService.getPaymentVerifications(),
  });

  const performance = performanceData?.data || {};
  const bookedOrders = bookedOrdersData?.data || [];
  const payments = paymentVerificationsData?.data || [];

  const stats = {
    teamLeads: performance.teamLeads || performance.totalTeamLeads || 0,
    totalRevenue: performance.totalRevenue || performance.revenue || 0,
    bookedOrders: bookedOrders.length,
    pendingPayments: payments.filter(p => !p.verified || p.status === 'pending').length,
  };

  if (performanceLoading || bookedLoading || paymentsLoading) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-rose-500 to-pink-600 rounded-xl p-6 text-white">
          <h1 className="text-3xl font-bold mb-2">TL Manager Dashboard</h1>
          <p className="text-white/80">Manage Team Leads and oversee sales operations</p>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="w-16 h-16 border-4 border-rose-500 border-t-transparent rounded-full spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-rose-500 to-pink-600 rounded-xl p-6 text-white">
        <h1 className="text-3xl font-bold mb-2">TL Manager Dashboard</h1>
        <p className="text-white/80">Manage Team Leads and oversee sales operations</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Team Leads</p>
              <p className="text-2xl font-bold text-gray-900">{stats.teamLeads}</p>
            </div>
            <Users size={24} className="text-rose-600" />
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Total Revenue</p>
              <p className="text-2xl font-bold text-gray-900">₹{Number(stats.totalRevenue).toLocaleString('en-IN')}</p>
            </div>
            <DollarSign size={24} className="text-green-600" />
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Booked Orders</p>
              <p className="text-2xl font-bold text-gray-900">{stats.bookedOrders}</p>
            </div>
            <BookOpen size={24} className="text-blue-600" />
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Pending Payments</p>
              <p className="text-2xl font-bold text-gray-900">{stats.pendingPayments}</p>
            </div>
            <BarChart3 size={24} className="text-yellow-600" />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button 
            onClick={() => navigate('/tl_manager/performance')}
            className="px-4 py-3 bg-rose-500 text-white rounded-lg hover:bg-rose-600"
          >
            View Performance
          </button>
          <button 
            onClick={() => navigate('/tl_manager/payment-verification')}
            className="px-4 py-3 bg-pink-500 text-white rounded-lg hover:bg-pink-600"
          >
            Verify Payments
          </button>
          <button 
            onClick={() => navigate('/tl_manager/booked')}
            className="px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            View Booked Orders
          </button>
        </div>
      </div>
    </div>
  );
};

export default TLManagerDashboard;

